import type {
  McpCatalogEntry,
  McpDiscoveredResource,
  McpDiscoveredTool,
  McpServerConnection,
} from './types';

function tool(
  id: string,
  name: string,
  description: string,
  inputSchemaSummary: string,
  enabled = false
): McpDiscoveredTool {
  return { id, name, description, inputSchemaSummary, enabled };
}

function resource(
  id: string,
  uri: string,
  name: string,
  mimeType: string,
  description?: string,
  enabled = false
): McpDiscoveredResource {
  return { id, uri, name, mimeType, description, enabled };
}

/** Pre-baked discovery payloads keyed by catalog id or synthetic template id. */
export const DISCOVERY_BY_KEY: Record<
  string,
  { tools: McpDiscoveredTool[]; resources: McpDiscoveredResource[] }
> = {
  'cat-filesystem': {
    tools: [
      tool('t-fs-read', 'read_file', 'Read a file from the allowed directory', '{ path: string }'),
      tool('t-fs-write', 'write_file', 'Write content to a file', '{ path: string, content: string }'),
      tool('t-fs-list', 'list_directory', 'List files in a directory', '{ path: string }'),
      tool('t-fs-search', 'search_files', 'Search files by glob pattern', '{ pattern: string }'),
    ],
    resources: [
      resource('r-fs-root', 'file:///workspace', 'Workspace root', 'inode/directory', 'Allowed root'),
      resource('r-fs-readme', 'file:///workspace/README.md', 'README', 'text/markdown'),
    ],
  },
  'cat-github': {
    tools: [
      tool('t-gh-issues', 'list_issues', 'List repository issues', '{ owner, repo, state? }'),
      tool('t-gh-pr', 'create_pull_request', 'Open a pull request', '{ owner, repo, title, head, base }'),
      tool('t-gh-search', 'search_code', 'Search code in a repo', '{ query: string }'),
      tool('t-gh-file', 'get_file_contents', 'Get file contents', '{ owner, repo, path }'),
    ],
    resources: [
      resource('r-gh-repo', 'github://owner/repo', 'Repository', 'application/vnd.github+json'),
      resource('r-gh-issues', 'github://owner/repo/issues', 'Issues feed', 'application/json'),
    ],
  },
  'cat-postgres': {
    tools: [
      tool('t-pg-query', 'query', 'Run a read-only SQL query', '{ sql: string }'),
      tool('t-pg-schema', 'list_tables', 'List tables in a schema', '{ schema?: string }'),
      tool('t-pg-explain', 'explain', 'Explain a query plan', '{ sql: string }'),
    ],
    resources: [
      resource('r-pg-schema', 'postgres://db/public', 'Public schema', 'application/sql'),
      resource('r-pg-meta', 'postgres://db/information_schema', 'Information schema', 'application/sql'),
    ],
  },
  'cat-slack': {
    tools: [
      tool('t-sl-post', 'post_message', 'Post a message to a channel', '{ channel, text }'),
      tool('t-sl-list', 'list_channels', 'List accessible channels', '{ limit?: number }'),
      tool('t-sl-history', 'get_channel_history', 'Fetch recent messages', '{ channel, limit? }'),
    ],
    resources: [
      resource('r-sl-channels', 'slack://workspace/channels', 'Channels', 'application/json'),
    ],
  },
  'cat-brave': {
    tools: [
      tool('t-br-web', 'brave_web_search', 'Search the web via Brave', '{ query: string, count?: number }'),
      tool('t-br-local', 'brave_local_search', 'Local/business search', '{ query: string }'),
    ],
    resources: [],
  },
  'cat-memory': {
    tools: [
      tool('t-mem-create', 'create_entities', 'Create knowledge graph entities', '{ entities: object[] }'),
      tool('t-mem-search', 'search_nodes', 'Search memory nodes', '{ query: string }'),
      tool('t-mem-open', 'open_nodes', 'Open nodes by name', '{ names: string[] }'),
    ],
    resources: [
      resource('r-mem-graph', 'memory://graph', 'Knowledge graph', 'application/json'),
    ],
  },
  'cat-puppeteer': {
    tools: [
      tool('t-pp-nav', 'puppeteer_navigate', 'Navigate to a URL', '{ url: string }'),
      tool('t-pp-shot', 'puppeteer_screenshot', 'Capture a screenshot', '{ name: string }'),
      tool('t-pp-click', 'puppeteer_click', 'Click an element', '{ selector: string }'),
      tool('t-pp-eval', 'puppeteer_evaluate', 'Evaluate JS in page', '{ script: string }'),
    ],
    resources: [
      resource('r-pp-page', 'puppeteer://page', 'Current page', 'text/html'),
    ],
  },
  'cat-notion': {
    tools: [
      tool('t-no-search', 'search', 'Search Notion workspace', '{ query: string }'),
      tool('t-no-page', 'get_page', 'Get a page by id', '{ pageId: string }'),
      tool('t-no-create', 'create_page', 'Create a page', '{ parentId, title, content? }'),
    ],
    resources: [
      resource('r-no-ws', 'notion://workspace', 'Workspace', 'application/json'),
      resource('r-no-db', 'notion://database/default', 'Default database', 'application/json'),
    ],
  },
  'cat-remote-sse': {
    tools: [
      tool('t-acme-metrics', 'get_metrics', 'Fetch product metrics', '{ range: string }'),
      tool('t-acme-funnel', 'get_funnel', 'Conversion funnel snapshot', '{ funnelId: string }'),
    ],
    resources: [
      resource('r-acme-ds', 'acme://datasets/default', 'Default dataset', 'application/json'),
    ],
  },
  'custom-default': {
    tools: [
      tool('t-custom-echo', 'echo', 'Echo a message (synthetic)', '{ message: string }'),
      tool('t-custom-ping', 'ping', 'Health check tool', '{}'),
    ],
    resources: [
      resource('r-custom-info', 'mcp://server/info', 'Server info', 'application/json'),
    ],
  },
};

export const CATALOG_ENTRIES: McpCatalogEntry[] = [
  {
    id: 'cat-filesystem',
    name: 'Filesystem',
    description: 'Secure file operations with configurable allowlists for local directories.',
    publisher: 'Model Context Protocol',
    repoUrl: 'https://github.com/modelcontextprotocol/servers/tree/main/src/filesystem',
    transport: 'stdio',
    defaultConfig: {
      transport: 'stdio',
      command: 'npx',
      args: ['-y', '@modelcontextprotocol/server-filesystem', '/workspace'],
      authType: 'none',
      tags: ['files', 'local'],
    },
    toolCount: 4,
    resourceCount: 2,
    tags: ['files', 'local', 'stdio'],
  },
  {
    id: 'cat-github',
    name: 'GitHub',
    description: 'Repository issues, PRs, code search, and file contents via the GitHub API.',
    publisher: 'Model Context Protocol',
    repoUrl: 'https://github.com/modelcontextprotocol/servers/tree/main/src/github',
    transport: 'stdio',
    defaultConfig: {
      transport: 'stdio',
      command: 'npx',
      args: ['-y', '@modelcontextprotocol/server-github'],
      env: { GITHUB_PERSONAL_ACCESS_TOKEN: '' },
      authType: 'none',
      tags: ['git', 'devtools'],
    },
    toolCount: 4,
    resourceCount: 2,
    tags: ['git', 'devtools', 'stdio'],
  },
  {
    id: 'cat-postgres',
    name: 'PostgreSQL',
    description: 'Read-only SQL queries and schema inspection against Postgres.',
    publisher: 'Model Context Protocol',
    repoUrl: 'https://github.com/modelcontextprotocol/servers/tree/main/src/postgres',
    transport: 'stdio',
    defaultConfig: {
      transport: 'stdio',
      command: 'npx',
      args: ['-y', '@modelcontextprotocol/server-postgres', 'postgresql://localhost/mydb'],
      authType: 'none',
      tags: ['database', 'sql'],
    },
    toolCount: 3,
    resourceCount: 2,
    tags: ['database', 'sql', 'stdio'],
  },
  {
    id: 'cat-slack',
    name: 'Slack',
    description: 'Post messages and read channel history in a Slack workspace.',
    publisher: 'Model Context Protocol',
    repoUrl: 'https://github.com/modelcontextprotocol/servers/tree/main/src/slack',
    transport: 'stdio',
    defaultConfig: {
      transport: 'stdio',
      command: 'npx',
      args: ['-y', '@modelcontextprotocol/server-slack'],
      env: { SLACK_BOT_TOKEN: '', SLACK_TEAM_ID: '' },
      authType: 'none',
      tags: ['chat', 'collaboration'],
    },
    toolCount: 3,
    resourceCount: 1,
    tags: ['chat', 'collaboration', 'stdio'],
  },
  {
    id: 'cat-brave',
    name: 'Brave Search',
    description: 'Web and local search powered by the Brave Search API.',
    publisher: 'Model Context Protocol',
    repoUrl: 'https://github.com/modelcontextprotocol/servers/tree/main/src/brave-search',
    transport: 'stdio',
    defaultConfig: {
      transport: 'stdio',
      command: 'npx',
      args: ['-y', '@modelcontextprotocol/server-brave-search'],
      env: { BRAVE_API_KEY: '' },
      authType: 'none',
      tags: ['search', 'web'],
    },
    toolCount: 2,
    resourceCount: 0,
    tags: ['search', 'web', 'stdio'],
  },
  {
    id: 'cat-memory',
    name: 'Memory',
    description: 'Persistent knowledge-graph memory for entities and relations.',
    publisher: 'Model Context Protocol',
    repoUrl: 'https://github.com/modelcontextprotocol/servers/tree/main/src/memory',
    transport: 'stdio',
    defaultConfig: {
      transport: 'stdio',
      command: 'npx',
      args: ['-y', '@modelcontextprotocol/server-memory'],
      authType: 'none',
      tags: ['memory', 'graph'],
    },
    toolCount: 3,
    resourceCount: 1,
    tags: ['memory', 'graph', 'stdio'],
  },
  {
    id: 'cat-puppeteer',
    name: 'Puppeteer',
    description: 'Browser automation: navigate, click, screenshot, and evaluate scripts.',
    publisher: 'Model Context Protocol',
    repoUrl: 'https://github.com/modelcontextprotocol/servers/tree/main/src/puppeteer',
    transport: 'stdio',
    defaultConfig: {
      transport: 'stdio',
      command: 'npx',
      args: ['-y', '@modelcontextprotocol/server-puppeteer'],
      authType: 'none',
      tags: ['browser', 'automation'],
    },
    toolCount: 4,
    resourceCount: 1,
    tags: ['browser', 'automation', 'stdio'],
  },
  {
    id: 'cat-notion',
    name: 'Notion',
    description: 'Search and manage pages in a Notion workspace over HTTP.',
    publisher: 'Community',
    repoUrl: 'https://github.com/suekou/mcp-notion-server',
    transport: 'streamable-http',
    defaultConfig: {
      transport: 'streamable-http',
      url: 'https://mcp.notion.example/mcp',
      authType: 'bearer',
      authValue: '',
      tags: ['docs', 'productivity'],
    },
    toolCount: 3,
    resourceCount: 2,
    tags: ['docs', 'productivity', 'http'],
  },
  {
    id: 'cat-remote-sse',
    name: 'Acme Analytics MCP',
    description: 'Example remote SSE MCP server exposing analytics tools and datasets.',
    publisher: 'Acme Corp',
    repoUrl: 'https://github.com/acme/analytics-mcp',
    transport: 'sse',
    defaultConfig: {
      transport: 'sse',
      url: 'https://mcp.acme.example/sse',
      headers: { 'X-Tenant': 'demo' },
      authType: 'apiKey',
      authValue: '',
      tags: ['analytics', 'remote'],
    },
    toolCount: 2,
    resourceCount: 1,
    tags: ['analytics', 'remote', 'sse'],
  },
];

const now = () => new Date().toISOString();

function cloneDiscovery(key: string, enableFirst = false) {
  const payload = DISCOVERY_BY_KEY[key] ?? DISCOVERY_BY_KEY['custom-default'];
  return {
    tools: payload.tools.map((t, i) => ({ ...t, enabled: enableFirst ? i === 0 : t.enabled })),
    resources: payload.resources.map((r, i) => ({
      ...r,
      enabled: enableFirst ? i === 0 : r.enabled,
    })),
  };
}

export function getDiscoveryForConnection(conn: Pick<McpServerConnection, 'catalogId' | 'source'>): {
  tools: McpDiscoveredTool[];
  resources: McpDiscoveredResource[];
} {
  const key = conn.catalogId && DISCOVERY_BY_KEY[conn.catalogId] ? conn.catalogId : 'custom-default';
  return cloneDiscovery(key);
}

export function createSeedConnections(): McpServerConnection[] {
  const ts = now();
  const fs = cloneDiscovery('cat-filesystem', true);
  const gh = cloneDiscovery('cat-github', true);
  const pg = cloneDiscovery('cat-postgres');
  const mem = cloneDiscovery('cat-memory', true);
  const custom = cloneDiscovery('custom-default');

  return [
    {
      id: 'conn-fs-1',
      name: 'Workspace Filesystem',
      description: 'Local workspace file access for agents.',
      transport: 'stdio',
      command: 'npx',
      args: ['-y', '@modelcontextprotocol/server-filesystem', '/workspace'],
      authType: 'none',
      tags: ['files', 'local'],
      isActive: true,
      source: 'catalog',
      catalogId: 'cat-filesystem',
      status: 'connected',
      lastTestedAt: ts,
      tools: fs.tools,
      resources: fs.resources,
      createdAt: ts,
      updatedAt: ts,
    },
    {
      id: 'conn-gh-1',
      name: 'GitHub (org)',
      description: 'Org-wide GitHub MCP server.',
      transport: 'stdio',
      command: 'npx',
      args: ['-y', '@modelcontextprotocol/server-github'],
      env: { GITHUB_PERSONAL_ACCESS_TOKEN: 'ghp_••••••••' },
      authType: 'none',
      tags: ['git', 'devtools'],
      isActive: true,
      source: 'catalog',
      catalogId: 'cat-github',
      status: 'connected',
      lastTestedAt: ts,
      tools: gh.tools,
      resources: gh.resources,
      createdAt: ts,
      updatedAt: ts,
    },
    {
      id: 'conn-pg-1',
      name: 'Analytics Postgres',
      description: 'Read-only warehouse connection.',
      transport: 'stdio',
      command: 'npx',
      args: ['-y', '@modelcontextprotocol/server-postgres', 'postgresql://localhost/analytics'],
      authType: 'none',
      tags: ['database', 'sql'],
      isActive: true,
      source: 'catalog',
      catalogId: 'cat-postgres',
      status: 'never_tested',
      tools: [],
      resources: [],
      createdAt: ts,
      updatedAt: ts,
    },
    {
      id: 'conn-mem-1',
      name: 'Agent Memory',
      description: 'Shared knowledge-graph memory.',
      transport: 'stdio',
      command: 'npx',
      args: ['-y', '@modelcontextprotocol/server-memory'],
      authType: 'none',
      tags: ['memory'],
      isActive: true,
      source: 'catalog',
      catalogId: 'cat-memory',
      status: 'connected',
      lastTestedAt: ts,
      tools: mem.tools,
      resources: mem.resources,
      createdAt: ts,
      updatedAt: ts,
    },
    {
      id: 'conn-custom-1',
      name: 'Internal Tools SSE',
      description: 'Custom remote MCP over SSE.',
      transport: 'sse',
      url: 'https://mcp.internal.example/sse',
      headers: { 'X-Env': 'staging' },
      authType: 'bearer',
      authValue: 'tok_••••••••',
      tags: ['custom', 'remote'],
      isActive: false,
      source: 'custom',
      status: 'failed',
      lastTestedAt: ts,
      lastError: 'Connection timed out (synthetic)',
      tools: custom.tools.map((t) => ({ ...t, enabled: false })),
      resources: custom.resources.map((r) => ({ ...r, enabled: false })),
      createdAt: ts,
      updatedAt: ts,
    },
  ];
}
